// SNIGJ - Admin Branding Page - Logo, colors, login page, document headers
import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAppearance } from '../context/AppearanceContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Image, Palette, FileText, LogIn, Save, RefreshCw, Upload,
  Scale, Eye, Building2, Trash2, Check, Shield, Key, Apple
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// Default government logo
const DEFAULT_GOV_LOGO = 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6f/Coat_of_arms_of_the_Democratic_Republic_of_the_Congo.svg/200px-Coat_of_arms_of_the_Democratic_Republic_of_the_Congo.svg.png';

const AdminBrandingPage = () => {
  const { user, language } = useAuth();
  const { settings: appearanceSettings, updateSettings: updateAppearance } = useAppearance();
  
  const [branding, setBranding] = useState({
    // Logo
    logoUrl: '',
    logoText: 'SNIGJ',
    logoSubtext: 'RDC',
    
    // Government Logo (NEW)
    governmentLogoUrl: DEFAULT_GOV_LOGO,
    
    // Login page
    loginImageUrl: '',
    loginHeaderText: 'Système National Intégré de Gestion de la Justice',
    loginFooterText: 'République Démocratique du Congo - Ministère de la Justice',
    infoPanelText: '', // Text for procedure information panel
    
    // OAuth Configuration (NEW)
    googleClientId: '',
    googleClientSecret: '',
    appleClientId: '',
    appleTeamId: '',
    appleKeyId: '',
    
    // Document branding
    documentHeaderHtml: '<div style="text-align:center"><strong>RÉPUBLIQUE DÉMOCRATIQUE DU CONGO</strong><br/>Ministère de la Justice</div>',
    documentFooterHtml: '<div style="text-align:center;font-size:10px">Document généré par SNIGJ - Système National Intégré de Gestion de la Justice</div>',
    ministryLogoUrl: '',
    jurisdictionLogoUrl: ''
  });
  
  const [hierarchy, setHierarchy] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const logoInputRef = useRef(null);
  const govLogoInputRef = useRef(null);
  const loginImageRef = useRef(null);
  const ministryLogoRef = useRef(null);
  const jurisdictionLogoRef = useRef(null);

  useEffect(() => {
    loadBranding();
    loadHierarchy();
  }, []);

  const loadBranding = async () => {
    try {
      const response = await api.get('/admin/branding');
      if (response.data) {
        setBranding(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Failed to load branding:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadHierarchy = async () => {
    try {
      const response = await api.get('/admin/hierarchy');
      setHierarchy(response.data.hierarchy || {});
    } catch (error) {
      console.error('Failed to load hierarchy:', error);
    }
  };

  const handleSaveBranding = async () => {
    setSaving(true);
    try {
      await api.put('/admin/branding', branding);
      toast.success(language === 'fr' ? 'Configuration enregistrée' : 'Configuration saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveHierarchy = async () => {
    setSaving(true);
    try {
      await api.put('/admin/hierarchy', { hierarchy });
      toast.success(language === 'fr' ? 'Hiérarchie mise à jour' : 'Hierarchy updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving');
    } finally {
      setSaving(false);
    }
  };

  const handleResetHierarchy = async () => {
    if (!confirm(language === 'fr' ? 'Réinitialiser la hiérarchie aux valeurs par défaut?' : 'Reset hierarchy to default values?')) return;
    
    try {
      const response = await api.post('/admin/hierarchy/reset');
      setHierarchy(response.data.hierarchy || {});
      toast.success(language === 'fr' ? 'Hiérarchie réinitialisée' : 'Hierarchy reset');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleImageUpload = (field, e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error(language === 'fr' ? 'Image trop grande (max 2MB)' : 'Image too large (max 2MB)');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setBranding(prev => ({ ...prev, [field]: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleHierarchyChange = (role, value) => {
    setHierarchy(prev => ({
      ...prev,
      [role]: parseInt(value) || 0
    }));
  };

  // Group hierarchy by category
  const hierarchyByCategory = Object.entries(hierarchy).reduce((acc, [role, level]) => {
    // Simple categorization
    let category = 'OTHER';
    if (role.includes('POLICE') || role.includes('OPJ') || role.includes('COMMISSAIRE')) category = 'POLICE';
    else if (role.includes('GREFFIER') || role.includes('JUGE') || role.includes('TRIBUNAL')) category = 'TRIBUNAUX';
    else if (role.includes('PROCUREUR') || role.includes('PARQUET') || role.includes('AVOCAT_GENERAL')) category = 'PARQUET';
    else if (role.includes('CASSATION') || role.includes('SUPREME') || role.includes('CONSEIL_ETAT')) category = 'COURS_NATIONALES';
    else if (role.includes('PRISON') || role.includes('CACHOT') || role.includes('GARDIEN')) category = 'PRISON';
    else if (role.includes('ADMIN') || role.includes('TECH') || role.includes('MINISTER') || role === 'PRESIDENT') category = 'EXECUTIVE';
    
    if (!acc[category]) acc[category] = [];
    acc[category].push({ role, level });
    return acc;
  }, {});

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl" data-testid="admin-branding-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
              <Palette className="w-8 h-8 text-primary" />
              {language === 'fr' ? 'Personnalisation & Branding' : 'Customization & Branding'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' 
                ? 'Configurez le logo, les couleurs et les textes de l\'application'
                : 'Configure the logo, colors and texts of the application'}
            </p>
          </div>
        </div>

        <Tabs defaultValue="branding" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="branding" className="gap-2">
              <Image className="w-4 h-4" />
              {language === 'fr' ? 'Logo & Images' : 'Logo & Images'}
            </TabsTrigger>
            <TabsTrigger value="oauth" className="gap-2">
              <Key className="w-4 h-4" />
              OAuth
            </TabsTrigger>
            <TabsTrigger value="documents" className="gap-2">
              <FileText className="w-4 h-4" />
              {language === 'fr' ? 'Documents' : 'Documents'}
            </TabsTrigger>
            <TabsTrigger value="hierarchy" className="gap-2">
              <Building2 className="w-4 h-4" />
              {language === 'fr' ? 'Hiérarchie' : 'Hierarchy'}
            </TabsTrigger>
          </TabsList>

          {/* Branding Tab */}
          <TabsContent value="branding" className="space-y-6">
            {/* Government Logo - NEW */}
            <Card className="border-2 border-yellow-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-yellow-600" />
                  {language === 'fr' ? 'Logo Gouvernemental (Armoiries RDC)' : 'Government Logo (DRC Coat of Arms)'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Le logo officiel affiché sur la page de connexion (armoiries de la RDC)'
                    : 'The official logo displayed on the login page (DRC coat of arms)'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-6">
                  <div className="w-24 h-24 rounded-full bg-white flex items-center justify-center border-2 border-dashed border-yellow-400 shadow-lg overflow-hidden">
                    <img 
                      src={branding.governmentLogoUrl || DEFAULT_GOV_LOGO} 
                      alt="Armoiries RDC" 
                      className="w-20 h-20 object-contain"
                      onError={(e) => { e.target.src = DEFAULT_GOV_LOGO; }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Button variant="outline" onClick={() => govLogoInputRef.current?.click()} className="gap-2">
                      <Upload className="w-4 h-4" />
                      {language === 'fr' ? 'Changer le logo gouvernemental' : 'Change government logo'}
                    </Button>
                    <input 
                      ref={govLogoInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                      className="hidden"
                      onChange={(e) => handleImageUpload('governmentLogoUrl', e)}
                    />
                    <p className="text-xs text-muted-foreground">
                      {language === 'fr' 
                        ? 'Formats acceptés: PNG, JPG, SVG, WebP (max 2MB)'
                        : 'Accepted formats: PNG, JPG, SVG, WebP (max 2MB)'}
                    </p>
                    {branding.governmentLogoUrl && branding.governmentLogoUrl !== DEFAULT_GOV_LOGO && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setBranding(prev => ({ ...prev, governmentLogoUrl: DEFAULT_GOV_LOGO }))}
                        className="text-red-500"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        {language === 'fr' ? 'Réinitialiser' : 'Reset'}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Application Logo */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Scale className="w-5 h-5" />
                  {language === 'fr' ? 'Logo de l\'Application' : 'Application Logo'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Le logo affiché dans la barre de navigation'
                    : 'The logo displayed in the navigation bar'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-6">
                  <div 
                    className="w-20 h-20 rounded-lg flex items-center justify-center border-2 border-dashed border-border"
                    style={{ backgroundColor: appearanceSettings?.primaryColor || '#eab308' }}
                  >
                    {branding.logoUrl ? (
                      <img src={branding.logoUrl} alt="Logo" className="w-16 h-16 object-contain" />
                    ) : (
                      <Scale className="w-10 h-10" style={{ color: appearanceSettings?.secondaryColor || '#0f172a' }} />
                    )}
                  </div>
                  <div className="space-y-2">
                    <Button variant="outline" onClick={() => logoInputRef.current?.click()} className="gap-2">
                      <Upload className="w-4 h-4" />
                      {language === 'fr' ? 'Changer le logo' : 'Change logo'}
                    </Button>
                    <input 
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImageUpload('logoUrl', e)}
                    />
                    {branding.logoUrl && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setBranding(prev => ({ ...prev, logoUrl: '' }))}
                        className="text-red-500"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        {language === 'fr' ? 'Supprimer' : 'Remove'}
                      </Button>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Texte principal' : 'Main text'}</Label>
                    <Input 
                      value={branding.logoText}
                      onChange={(e) => setBranding(prev => ({ ...prev, logoText: e.target.value }))}
                      placeholder="SNIGJ"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Sous-texte' : 'Subtext'}</Label>
                    <Input 
                      value={branding.logoSubtext}
                      onChange={(e) => setBranding(prev => ({ ...prev, logoSubtext: e.target.value }))}
                      placeholder="RDC"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Login Page */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <LogIn className="w-5 h-5" />
                  {language === 'fr' ? 'Page de Connexion' : 'Login Page'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Personnalisez l\'apparence de la page de connexion'
                    : 'Customize the appearance of the login page'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Government Logo Size */}
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Taille du Logo Gouvernemental (px)' : 'Government Logo Size (px)'}</Label>
                  <div className="flex items-center gap-4">
                    <Input 
                      type="number"
                      value={branding.governmentLogoSize || 120}
                      onChange={(e) => setBranding(prev => ({ ...prev, governmentLogoSize: parseInt(e.target.value) || 120 }))}
                      min={60}
                      max={200}
                      className="w-32"
                    />
                    <span className="text-sm text-muted-foreground">
                      {language === 'fr' ? '(min: 60px, max: 200px)' : '(min: 60px, max: 200px)'}
                    </span>
                  </div>
                </div>

                <Separator />

                {/* Login Image */}
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Image de la page de connexion' : 'Login page image'}</Label>
                  <div className="flex items-center gap-4">
                    <div className="w-40 h-24 rounded border-2 border-dashed border-border bg-muted flex items-center justify-center overflow-hidden">
                      {branding.loginImageUrl ? (
                        <img src={branding.loginImageUrl} alt="Login" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-xs text-muted-foreground text-center p-2">
                          {language === 'fr' ? 'Aucune image' : 'No image'}
                        </span>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Button variant="outline" size="sm" onClick={() => loginImageRef.current?.click()} className="gap-2">
                        <Upload className="w-4 h-4" />
                        {language === 'fr' ? 'Choisir une image' : 'Choose image'}
                      </Button>
                      <input 
                        ref={loginImageRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload('loginImageUrl', e)}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Titre de la page de connexion' : 'Login page title'}</Label>
                  <Input 
                    value={branding.loginHeaderText}
                    onChange={(e) => setBranding(prev => ({ ...prev, loginHeaderText: e.target.value }))}
                    placeholder="Système National Intégré de Gestion de la Justice"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Pied de page de connexion' : 'Login page footer'}</Label>
                  <Input 
                    value={branding.loginFooterText}
                    onChange={(e) => setBranding(prev => ({ ...prev, loginFooterText: e.target.value }))}
                    placeholder="République Démocratique du Congo"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Marquee Banner Configuration */}
            <Card className="border-2 border-blue-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  {language === 'fr' ? 'Bannière Défilante (Informations Procédures)' : 'Scrolling Banner (Procedure Information)'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Configurez la bannière d\'informations légales affichée en haut de la page de connexion'
                    : 'Configure the legal information banner displayed at the top of the login page'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
                  <div>
                    <Label className="text-base">{language === 'fr' ? 'Activer la bannière' : 'Enable banner'}</Label>
                    <p className="text-sm text-muted-foreground">
                      {language === 'fr' 
                        ? 'Affiche une bannière défilante avec des informations légales'
                        : 'Shows a scrolling banner with legal information'}
                    </p>
                  </div>
                  <Button
                    variant={branding.showMarqueeBanner ? "default" : "outline"}
                    size="sm"
                    onClick={() => setBranding(prev => ({ ...prev, showMarqueeBanner: !prev.showMarqueeBanner }))}
                  >
                    {branding.showMarqueeBanner 
                      ? (language === 'fr' ? 'Activé' : 'Enabled')
                      : (language === 'fr' ? 'Désactivé' : 'Disabled')}
                  </Button>
                </div>

                {branding.showMarqueeBanner && (
                  <>
                    {/* Banner Text */}
                    <div className="space-y-2">
                      <Label>{language === 'fr' ? 'Texte de la bannière' : 'Banner text'}</Label>
                      <Textarea 
                        value={branding.marqueeBannerText || ''}
                        onChange={(e) => setBranding(prev => ({ ...prev, marqueeBannerText: e.target.value }))}
                        placeholder={language === 'fr' 
                          ? 'Ex: 📜 La garde à vue est limitée à 48h. Tout citoyen a droit à un avocat...'
                          : 'Ex: 📜 Custody is limited to 48h. Every citizen has the right to a lawyer...'}
                        rows={3}
                      />
                      <p className="text-xs text-muted-foreground">
                        {language === 'fr' 
                          ? 'Utilisez | pour séparer les différents messages. Vous pouvez utiliser des emojis.'
                          : 'Use | to separate different messages. You can use emojis.'}
                      </p>
                    </div>

                    {/* Banner Speed - SLIDER */}
                    <div className="space-y-3">
                      <Label>{language === 'fr' ? 'Vitesse de défilement' : 'Scroll speed'}</Label>
                      <div className="space-y-4">
                        {/* Slider */}
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-muted-foreground w-16">{language === 'fr' ? 'Rapide' : 'Fast'}</span>
                          <input
                            type="range"
                            min="20"
                            max="200"
                            step="5"
                            value={branding.marqueeBannerSpeedValue || 80}
                            onChange={(e) => setBranding(prev => ({ 
                              ...prev, 
                              marqueeBannerSpeedValue: parseInt(e.target.value),
                              marqueeBannerSpeed: null // Clear preset when using slider
                            }))}
                            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 accent-blue-600"
                          />
                          <span className="text-sm text-muted-foreground w-16 text-right">{language === 'fr' ? 'Lent' : 'Slow'}</span>
                        </div>
                        {/* Current value display */}
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">
                            {language === 'fr' ? 'Durée du cycle' : 'Cycle duration'}: <strong>{branding.marqueeBannerSpeedValue || 80}s</strong>
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setBranding(prev => ({ ...prev, marqueeBannerSpeedValue: 80 }))}
                            className="text-xs"
                          >
                            {language === 'fr' ? 'Réinitialiser (80s)' : 'Reset (80s)'}
                          </Button>
                        </div>
                        {/* Preset buttons (optional quick select) */}
                        <div className="flex gap-2">
                          <span className="text-xs text-muted-foreground">{language === 'fr' ? 'Presets:' : 'Presets:'}</span>
                          {[
                            { label: language === 'fr' ? 'Très lent' : 'Very slow', value: 180 },
                            { label: language === 'fr' ? 'Lent' : 'Slow', value: 120 },
                            { label: language === 'fr' ? 'Normal' : 'Normal', value: 80 },
                            { label: language === 'fr' ? 'Rapide' : 'Fast', value: 40 },
                          ].map((preset) => (
                            <Button
                              key={preset.value}
                              variant={(branding.marqueeBannerSpeedValue || 80) === preset.value ? "default" : "outline"}
                              size="sm"
                              onClick={() => setBranding(prev => ({ ...prev, marqueeBannerSpeedValue: preset.value }))}
                              className="text-xs"
                            >
                              {preset.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Banner Color */}
                    <div className="space-y-2">
                      <Label>{language === 'fr' ? 'Couleur de fond' : 'Background color'}</Label>
                      <div className="flex items-center gap-3">
                        <input 
                          type="color"
                          value={branding.marqueeBannerColor || '#1e3a5f'}
                          onChange={(e) => setBranding(prev => ({ ...prev, marqueeBannerColor: e.target.value }))}
                          className="w-12 h-10 rounded cursor-pointer"
                        />
                        <Input 
                          value={branding.marqueeBannerColor || '#1e3a5f'}
                          onChange={(e) => setBranding(prev => ({ ...prev, marqueeBannerColor: e.target.value }))}
                          className="w-32"
                          placeholder="#1e3a5f"
                        />
                      </div>
                    </div>

                    {/* Preview */}
                    <div className="space-y-2">
                      <Label>{language === 'fr' ? 'Aperçu' : 'Preview'}</Label>
                      <div 
                        className="w-full py-2 text-white text-sm overflow-hidden rounded"
                        style={{ backgroundColor: branding.marqueeBannerColor || '#1e3a5f' }}
                      >
                        <div className="whitespace-nowrap animate-pulse">
                          {branding.marqueeBannerText || (language === 'fr' ? 'Texte de la bannière...' : 'Banner text...')}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button onClick={handleSaveBranding} disabled={saving} className="gap-2">
                <Save className="w-4 h-4" />
                {saving ? (language === 'fr' ? 'Enregistrement...' : 'Saving...') : (language === 'fr' ? 'Enregistrer' : 'Save')}
              </Button>
            </div>
          </TabsContent>

          {/* OAuth Tab - NEW */}
          <TabsContent value="oauth" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google OAuth
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Configurez la connexion via Google. Obtenez vos clés sur console.cloud.google.com'
                    : 'Configure Google login. Get your keys from console.cloud.google.com'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Google Client ID</Label>
                  <Input 
                    value={branding.googleClientId || ''}
                    onChange={(e) => setBranding(prev => ({ ...prev, googleClientId: e.target.value }))}
                    placeholder="xxx.apps.googleusercontent.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Google Client Secret</Label>
                  <Input 
                    type="password"
                    value={branding.googleClientSecret || ''}
                    onChange={(e) => setBranding(prev => ({ ...prev, googleClientSecret: e.target.value }))}
                    placeholder="GOCSPX-xxx"
                  />
                </div>
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    {language === 'fr' 
                      ? '💡 Pour obtenir ces clés: 1) Allez sur console.cloud.google.com 2) Créez un projet 3) Activez "Google+ API" 4) Créez des identifiants OAuth 2.0'
                      : '💡 To get these keys: 1) Go to console.cloud.google.com 2) Create a project 3) Enable "Google+ API" 4) Create OAuth 2.0 credentials'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Apple className="w-5 h-5" />
                  Apple Sign-In
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Configurez la connexion via Apple. Nécessite un compte Apple Developer ($99/an)'
                    : 'Configure Apple login. Requires an Apple Developer account ($99/year)'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Apple Client ID (Service ID)</Label>
                  <Input 
                    value={branding.appleClientId || ''}
                    onChange={(e) => setBranding(prev => ({ ...prev, appleClientId: e.target.value }))}
                    placeholder="com.yourapp.service"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Team ID</Label>
                    <Input 
                      value={branding.appleTeamId || ''}
                      onChange={(e) => setBranding(prev => ({ ...prev, appleTeamId: e.target.value }))}
                      placeholder="ABCD1234EF"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Key ID</Label>
                    <Input 
                      value={branding.appleKeyId || ''}
                      onChange={(e) => setBranding(prev => ({ ...prev, appleKeyId: e.target.value }))}
                      placeholder="ABC123DEFG"
                    />
                  </div>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-900/20 rounded-lg">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    {language === 'fr' 
                      ? '💡 Apple Sign-In nécessite un compte Apple Developer. Configurez sur developer.apple.com > Certificates, IDs & Profiles > Services IDs'
                      : '💡 Apple Sign-In requires an Apple Developer account. Configure at developer.apple.com > Certificates, IDs & Profiles > Services IDs'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button onClick={handleSaveBranding} disabled={saving} className="gap-2">
                <Save className="w-4 h-4" />
                {saving ? (language === 'fr' ? 'Enregistrement...' : 'Saving...') : (language === 'fr' ? 'Enregistrer' : 'Save')}
              </Button>
            </div>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  {language === 'fr' ? 'En-têtes et Pieds de Page des Documents' : 'Document Headers and Footers'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Personnalisez l\'en-tête et le pied de page de tous les documents imprimables'
                    : 'Customize the header and footer of all printable documents'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Document Logos */}
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Logo du Ministère' : 'Ministry Logo'}</Label>
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-16 rounded border flex items-center justify-center bg-muted overflow-hidden">
                        {branding.ministryLogoUrl ? (
                          <img src={branding.ministryLogoUrl} alt="Ministry" className="w-full h-full object-contain" />
                        ) : (
                          <Building2 className="w-8 h-8 text-muted-foreground" />
                        )}
                      </div>
                      <Button variant="outline" size="sm" onClick={() => ministryLogoRef.current?.click()}>
                        <Upload className="w-4 h-4" />
                      </Button>
                      <input 
                        ref={ministryLogoRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload('ministryLogoUrl', e)}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Logo de la Juridiction' : 'Jurisdiction Logo'}</Label>
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-16 rounded border flex items-center justify-center bg-muted overflow-hidden">
                        {branding.jurisdictionLogoUrl ? (
                          <img src={branding.jurisdictionLogoUrl} alt="Jurisdiction" className="w-full h-full object-contain" />
                        ) : (
                          <Scale className="w-8 h-8 text-muted-foreground" />
                        )}
                      </div>
                      <Button variant="outline" size="sm" onClick={() => jurisdictionLogoRef.current?.click()}>
                        <Upload className="w-4 h-4" />
                      </Button>
                      <input 
                        ref={jurisdictionLogoRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload('jurisdictionLogoUrl', e)}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'En-tête des documents (HTML)' : 'Document header (HTML)'}</Label>
                  <Textarea 
                    value={branding.documentHeaderHtml}
                    onChange={(e) => setBranding(prev => ({ ...prev, documentHeaderHtml: e.target.value }))}
                    rows={4}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    {language === 'fr' 
                      ? 'Vous pouvez utiliser du HTML pour formater l\'en-tête'
                      : 'You can use HTML to format the header'}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Pied de page des documents (HTML)' : 'Document footer (HTML)'}</Label>
                  <Textarea 
                    value={branding.documentFooterHtml}
                    onChange={(e) => setBranding(prev => ({ ...prev, documentFooterHtml: e.target.value }))}
                    rows={3}
                    className="font-mono text-sm"
                  />
                </div>

                {/* Preview */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    {language === 'fr' ? 'Aperçu' : 'Preview'}
                  </Label>
                  <div className="border rounded-lg p-4 bg-white text-black">
                    <div dangerouslySetInnerHTML={{ __html: branding.documentHeaderHtml }} className="border-b pb-2 mb-4" />
                    <div className="h-20 flex items-center justify-center text-muted-foreground text-sm">
                      [{language === 'fr' ? 'Contenu du document' : 'Document content'}]
                    </div>
                    <div dangerouslySetInnerHTML={{ __html: branding.documentFooterHtml }} className="border-t pt-2 mt-4" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button onClick={handleSaveBranding} disabled={saving} className="gap-2">
                <Save className="w-4 h-4" />
                {saving ? (language === 'fr' ? 'Enregistrement...' : 'Saving...') : (language === 'fr' ? 'Enregistrer' : 'Save')}
              </Button>
            </div>
          </TabsContent>

          {/* Hierarchy Tab */}
          <TabsContent value="hierarchy" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    {language === 'fr' ? 'Ordre Hiérarchique des Rôles' : 'Role Hierarchy Order'}
                  </span>
                  <Button variant="outline" size="sm" onClick={handleResetHierarchy} className="gap-2">
                    <RefreshCw className="w-4 h-4" />
                    {language === 'fr' ? 'Réinitialiser' : 'Reset'}
                  </Button>
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Définissez le niveau hiérarchique de chaque rôle (plus le nombre est élevé, plus le niveau est haut)'
                    : 'Define the hierarchical level of each role (higher number = higher level)'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {Object.entries(hierarchyByCategory).map(([category, roles]) => (
                  <div key={category} className="space-y-3">
                    <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                      {category.replace(/_/g, ' ')}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {roles.sort((a, b) => b.level - a.level).map(({ role, level }) => (
                        <div key={role} className="flex items-center gap-2 p-2 rounded border bg-muted/30">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{role}</p>
                          </div>
                          <Input 
                            type="number"
                            value={level}
                            onChange={(e) => handleHierarchyChange(role, e.target.value)}
                            className="w-20 h-8 text-center"
                            min={0}
                            max={100}
                          />
                        </div>
                      ))}
                    </div>
                    <Separator />
                  </div>
                ))}
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button onClick={handleSaveHierarchy} disabled={saving} className="gap-2">
                <Save className="w-4 h-4" />
                {saving ? (language === 'fr' ? 'Enregistrement...' : 'Saving...') : (language === 'fr' ? 'Enregistrer la hiérarchie' : 'Save Hierarchy')}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default AdminBrandingPage;
